<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none users-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in users"
        :key="item._id"
        class="users-mobile-card mt-8"
        :class="{
          'users-mobile-card--selected': index === activeRow,
        }"
        @click="activeRow = activeRow === index ? null : index"
      >
        <div class="users-mobile-card__main">
          <div class="users-mobile-card__info">
            <div class="users-mobile-card__info-top">
              <div class="users-mobile-card__header">
                <div class="users-mobile-card__header-left">
                  <div class="font-weight-bold text-primary text-body-2">
                    <v-icon size="18" class="mr-1">mdi-account</v-icon>
                    {{ item.name }} {{ item.surname }}
                  </div>

                  <div
                    v-if="item.email"
                    class="text-caption text-grey-darken-1 mt-1"
                  >
                    <v-icon size="16" class="mr-1">mdi-email</v-icon>
                    {{ item.email }}
                  </div>
                  <div
                    v-if="item.phone"
                    class="text-caption text-grey-darken-1 mt-1"
                  >
                    <v-icon size="16" class="mr-1">mdi-phone</v-icon>
                    {{ item.phone }}
                  </div>
                </div>

                <div class="users-mobile-card__header-right">
                  <v-btn
                    color="black"
                    variant="outlined"
                    readonly
                    class="users-mobile-card__created-btn"
                    @click.stop
                  >
                    {{ formatDate(item.date_created) }}
                  </v-btn>
                  <v-chip
                    size="small"
                    :color="item.disabled ? 'red' : 'green'"
                    class="users-mobile-card__status-chip"
                  >
                    {{ item.disabled ? $t("disabled") : $t("active") }}
                  </v-chip>
                </div>
              </div>

              <div class="text-caption mt-2">
                <span class="text-grey-darken-2 font-weight-medium">
                  {{ $t("users.table.last_login_date") }}:
                </span>
                {{ formatLastLoginLong(item.last_login_date) }}
              </div>
              <div class="mt-1">
                <v-chip size="small" variant="tonal">
                  <v-icon color="green">mdi-circle-medium</v-icon>
                  {{ formatLastLogin(item.last_login_date) }}
                </v-chip>
              </div>

              <div class="text-caption mt-2">
                <span class="text-grey-darken-2 font-weight-medium">
                  {{ $t("home.user_role") }}:
                </span>
                {{ getRole(item.role) }} -   {{ item.institution_id?.title || "" }}
              </div>

             

              <div class="mt-2">
                <div v-if="item._loading" class="d-flex align-center">
                  <v-progress-circular indeterminate color="red" size="18" />
                  <span class="text-caption text-grey-darken-1 ml-2">
                    {{ $t("users.table.parts") }}
                  </span>
                </div>
                <div v-else-if="item._parts || item.approved_parts_number" class="text-caption">
                  <div class="users-mobile-parts-row">
                    <div class="users-mobile-parts-item">
                      {{ $t("users.table.temporary") }}:
                      <strong>{{ item._parts?.InsertedTemporaryParts || 0 }}</strong>
                    </div>
                    <div class="users-mobile-parts-item text-right">
                      {{ $t("users.table.sold") }}:
                      <strong>{{ item._parts?.Sold || 0 }}</strong>
                    </div>
                  </div>
                  <div class="users-mobile-parts-row mt-1">
                    <div class="users-mobile-parts-item">
                      {{ $t("users.table.approved") }}:
                      <strong>
                        {{
                          item._parts?.ApprovedParts ||
                          item.approved_parts_number ||
                          0
                        }}
                      </strong>
                    </div>
                    <div class="users-mobile-parts-item text-right">
                      {{ $t("users.table.inventory") }}:
                      <strong>{{ item._parts?.InsertedInventory || 0 }}</strong>
                    </div>
                  </div>
                </div>
                <div v-else class="text-caption text-grey-darken-1">
                  {{ $t("users.table.no_parts") }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="users-mobile-card__actions">
          <v-spacer />
          <v-btn
            v-if="
              $store.getters.role === 'SuperAdmin' &&
              !item.disabled &&
              item._id !== $store.getters.userId
            "
            icon
            variant="text"
            size="small"
            @click.stop="disableUser(item._id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-lock</v-icon>
          </v-btn>
          <v-btn
            v-else-if="
              $store.getters.role === 'SuperAdmin' &&
              item.disabled &&
              item._id !== $store.getters.userId
            "
            icon
            variant="text"
            size="small"
            @click.stop="enableUser(item._id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-lock-off-outline</v-icon>
          </v-btn>
          <v-btn
            v-if="
              [
                'LekoAdmin',
                'LekoEmployee',
                'InstitutionAdmin',
                'SuperAdmin',
              ].includes($store.getters.role)
            "
            icon
            variant="text"
            size="small"
            @click.stop="editUser(item)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="
              [
                'LekoAdmin',
                'LekoEmployee',
                'InstitutionAdmin',
                'SuperAdmin',
              ].includes($store.getters.role) &&
              item._id !== $store.getters.userId
            "
            icon
            variant="text"
            size="small"
            @click.stop="deleteUser(item._id)"
          >
            <v-icon size="20" color="grey-darken-2">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <v-alert v-if="!loading && users.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <div class="users-table-host pb-2">
      <StickyHScroll ref="usersTableScroll">
        <v-data-table-server
          v-model:options="pagination"
          :headers="headers"
          :items="users"
          :items-length="totalItems"
          :page="pagination.page"
          :items-per-page="pagination.itemsPerPage"
          :items-per-page-options="[
            { value: 5, title: '5' },
            { value: 10, title: '10' },
            { value: 25, title: '25' },
            { value: 50, title: '50' },
          ]"
          items-per-page-text="Rows per page"
          :loading="loading"
          class="elevation-1 nowrap-headers pb-8"
          @update:options="updatePagination"
        >
          <template #progress>
            <v-progress-linear color="red" indeterminate />
          </template>
          <template #item="{ item, index }">
            <tr
              :class="{ tableRowColorSelected: index === activeRow }"
              class="tableRowColor"
              @click="activeRow = activeRow === index ? null : index"
            >
              <td>{{ index + 1 }}</td>
              <td>
                <div class="d-flex flex-column pa-4 text-truncate">
                  <div>
                    <v-icon>mdi-account</v-icon>
                    {{ item.name }} {{ item.surname }}
                  </div>
                  <div>
                    <v-icon>mdi-email</v-icon>
                    {{ item.email }}
                  </div>
                  <div>
                    <v-icon>mdi-phone</v-icon>
                    {{ item.phone }}
                  </div>
                  <div>
                    <v-icon>mdi-calendar</v-icon>
                    {{ formatDate(item.date_created) }}
                  </div>
                  <div v-if="$store.getters.role === 'SuperAdmin'">
                    <v-icon>mdi-card-account-details-outline</v-icon>
                    {{ getRole(item.role) }}
                  </div>
                </div>
              </td>
              <td>
                <v-progress-circular
                  v-if="item._loading"
                  indeterminate
                  color="red"
                />
                <div
                  v-else-if="item._parts || item.approved_parts_number"
                  class="d-flex flex-column pa-4 text-truncate"
                >
                  <div>
                    {{ $t("users.table.temporary") }}:
                    <strong>
                      {{ item._parts?.InsertedTemporaryParts || 0 }}
                    </strong>
                  </div>
                  <div>
                    {{ $t("users.table.approved") }}:
                    <strong>
                      {{
                        item._parts?.ApprovedParts ||
                        item.approved_parts_number ||
                        0
                      }}
                    </strong>
                  </div>
                  <div>
                    {{ $t("users.table.inventory") }}:
                    <strong>
                      {{ item._parts?.InsertedInventory || 0 }}
                    </strong>
                  </div>
                  <div>
                    {{ $t("users.table.sold") }}:
                    <strong>
                      {{ item._parts?.Sold || 0 }}
                    </strong>
                  </div>
                </div>
                <span v-else>{{ $t("users.table.no_parts") }}</span>
              </td>
              <td>
                <div class="d-flex flex-column text-truncate">
                  <div>
                    {{ formatLastLoginLong(item.last_login_date) }}
                  </div>
                  <div class="mt-1">
                    <v-chip>
                      <v-icon color="green">mdi-circle-medium</v-icon>
                      {{ formatLastLogin(item.last_login_date) }}
                    </v-chip>
                  </div>
                </div>
              </td>
              <td>
                <div class="d-block text-center pt-4">
                  <div>
                    <v-btn variant="outlined" size="small">
                      {{ formatDate(item.date_created) }}
                    </v-btn>
                  </div>
                  <div class="pt-2">
                    <v-chip :color="item.disabled ? 'red' : 'green'">
                      {{ item.disabled ? $t("disabled") : $t("active") }}
                    </v-chip>
                  </div>
                </div>
              </td>
              <td>{{ item.city || "N/A" }}</td>
              <td>{{ item.state || "N/A" }}</td>
              <td class="text-truncate">
                <v-icon>mdi-card-account-details-outline</v-icon>
                {{ getRole(item.role) }}
              </td>
              <td>
                <v-btn color="black" variant="outlined" size="small">
                  {{ item.institution_id?.title || "N/A" }}
                </v-btn>
              </td>
              <td>
                <div class="d-flex justify-center flex-nowrap">
                  <v-icon
                    v-if="
                      $store.getters.role === 'SuperAdmin' &&
                      !item.disabled &&
                      item._id !== $store.getters.userId
                    "
                    class="mr-2 pointer"
                    @click="disableUser(item._id)"
                  >
                    mdi-lock
                  </v-icon>
                  <v-icon
                    v-else-if="
                      $store.getters.role === 'SuperAdmin' &&
                      item.disabled &&
                      item._id !== $store.getters.userId
                    "
                    class="mr-2 pointer"
                    @click="enableUser(item._id)"
                  >
                    mdi-lock-off-outline
                  </v-icon>
                  <v-icon
                    v-if="
                      [
                        'LekoAdmin',
                        'LekoEmployee',
                        'InstitutionAdmin',
                        'SuperAdmin',
                      ].includes($store.getters.role)
                    "
                    class="mr-2 pointer"
                    @click="editUser(item)"
                  >
                    mdi-pencil
                  </v-icon>
                  <v-icon
                    v-if="
                      [
                        'LekoAdmin',
                        'LekoEmployee',
                        'InstitutionAdmin',
                        'SuperAdmin',
                      ].includes($store.getters.role) &&
                      item._id !== $store.getters.userId
                    "
                    class="pointer"
                    @click="deleteUser(item._id)"
                  >
                    mdi-delete
                  </v-icon>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table-server>
      </StickyHScroll>
    </div>
    <v-dialog v-model="dialogDelete" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            type="error"
            dense
            variant="tonal"
            border="bottom"
          >
            {{ $t("delete_user") }}
          </v-alert>
        </v-card-title>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete()">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import getRequest from "../../api/getRequest";
import deleteRequest from "../../api/deleteRequest";
import putRequest from "../../api/putRequest";

export default {
  name: "UsersTable",
  props: {
    search: {
      type: String,
      default: "",
    },
    institution: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loading: false,
      activeRow: null,
      pagination: {
        page: 1,
        itemsPerPage: 5,
        sortBy: [{ key: "_id", order: "desc" }],
      },
      headers: [
        { title: "#", key: "index", sortable: false },
        { title: "User", key: "name", sortable: true },
        { title: "Parts", key: "parts", sortable: true },
        { title: "Last Login", key: "last_login_date", sortable: true },
        { title: "Created", key: "date_created", sortable: true },
        { title: "City", key: "city", sortable: true },
        { title: "State", key: "state", sortable: true },
        { title: "Role", key: "role", sortable: true },
        { title: "Institution", key: "institution_id.title", sortable: true },
        { title: "Actions", key: "actions", sortable: false },
      ],
      users: [],
      totalItems: 0,
      dialogDelete: false,
      userToDelete: null,
      fetchTimeout: null,
    };
  },
  watch: {
    search() {
      this.debouncedFetch();
    },
    institution() {
      this.debouncedFetch();
    },
  },
  created() {
    this.getLocale();
  },
  mounted() {
    this.refreshTableScroll();
  },
  methods: {
    refreshTableScroll() {
      this.$nextTick(() => {
        const sticky = this.$refs.usersTableScroll;
        if (sticky && typeof sticky.refresh === "function") {
          sticky.refresh();
        }
      });
    },
    scheduleTableScrollRefresh() {
      if (this._scrollRefreshTimer) clearTimeout(this._scrollRefreshTimer);
      this._scrollRefreshTimer = setTimeout(() => this.refreshTableScroll(), 100);
    },
    debouncedFetch() {
      if (this.fetchTimeout) clearTimeout(this.fetchTimeout);
      this.fetchTimeout = setTimeout(() => {
        if (this.pagination.page === 1) {
          this.fetchUsers();
        } else {
          this.pagination.page = 1;
          // Setting page = 1 will trigger updatePagination via v-model:options,
          // and updatePagination will call fetchUsers().
        }
      }, 500);
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("D MMMM YYYY");
    },
    formatLastLogin(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).fromNow();
    },
    formatLastLoginLong(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
    getLocale() {
      this.headers = [
        { title: "#", key: "index", sortable: false },
        { title: this.$t("users.table.user"), key: "name", sortable: true },
        { title: this.$t("users.table.parts"), key: "parts", sortable: true },
        {
          title: this.$t("users.table.last_login_date"),
          key: "last_login_date",
          sortable: true,
        },
        {
          title: this.$t("users.table.date"),
          key: "date_created",
          sortable: true,
        },
        { title: this.$t("users.table.city"), key: "city", sortable: true },
        { title: this.$t("users.table.state"), key: "state", sortable: true },
        { title: this.$t("home.user_role"), key: "role", sortable: true },
        {
          title: this.$t("users.table.institution_id"),
          key: "institution_id.title",
          sortable: true,
        },
        {
          title: this.$t("users.table.actions"),
          key: "actions",
          sortable: false,
        },
      ];
    },
    async fetchUsers() {
      try {
        this.loading = true;
        const sort = this.pagination.sortBy?.[0] || {
          key: "_id",
          order: "desc",
        };

        const roleToFetch = this.institution
          ? "InstitutionAdmin"
          : this.$store.getters.role;
        const instId = this.institution || this.$store.getters.institutionId;

        const params = new URLSearchParams({
          role: roleToFetch,
          institution_id: instId,
          limit: this.pagination.itemsPerPage,
          page: this.pagination.page,
          sort_by: sort.key,
          descending: sort.order === "desc",
        });

        if (this.search) {
          params.append("search", this.search);
        }

        const response = await getRequest("/user/", params.toString());
        this.users = [...(response.users || [])];
        this.totalItems = response.count || 0;

        // Lazy load parts for each user
        this.users.forEach((user) => {
          user._parts = null;
          user._loading = false;
          this.getParts(user._id);
        });
      } catch (error) {
        console.error("Error fetching users:", error);
        this.users = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
        this.scheduleTableScrollRefresh();
      }
    },
    // Backwards compatibility for external calls
    getUsers() {
      this.fetchUsers();
    },
    async getParts(userId) {
      const index = this.users.findIndex((user) => user._id === userId);
      if (index === -1) {
        return;
      }
      this.users[index]._loading = true;
      try {
        const result = await getRequest(
          `/history/user/single/${userId}`,
          "status=1,1.4,2,6",
        );
        this.users[index]._parts = {
          InsertedTemporaryParts: result.InsertedTemporaryParts || 0,
          ApprovedParts: result.ApprovedParts || 0,
          InsertedInventory: result.InsertedInventory || 0,
          Sold: result.Sold || 0,
        };
      } catch (error) {
        this.users[index]._parts = {
          ApprovedParts: this.users[index].approved_parts_number || 0,
        };
      } finally {
        if (this.users[index]) {
          this.users[index]._loading = false;
          this.users = [...this.users]; // Force reactivity
          this.scheduleTableScrollRefresh();
        }
      }
    },
    editUser(data) {
      this.$emit("edit", data);
    },
    deleteUser(userId) {
      this.userToDelete = userId;
      this.dialogDelete = true;
    },
    async confirmDelete() {
      try {
        this.loading = true;
        await deleteRequest("/user/", `id=${this.userToDelete}`);
        this.dialogDelete = false;
        this.userToDelete = null;
        await this.fetchUsers();
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async disableUser(userId) {
      try {
        this.loading = true;
        await putRequest("/user/disable", `id=${userId}`, { disabled: true });
        await this.fetchUsers();
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async enableUser(userId) {
      try {
        this.loading = true;
        await putRequest("/user/disable", `id=${userId}`, { disabled: false });
        await this.fetchUsers();
      } catch {
      } finally {
        this.loading = false;
      }
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage || 5,
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : [{ key: "_id", order: "desc" }],
      };

      this.activeRow = null;
      this.fetchUsers();
      this.scheduleTableScrollRefresh();
    },
    getRole(role) {
      switch (role) {
        case "LekoEmployee":
          return this.$t("users.table.leko_digitalization_employee");
        case "InstitutionAdmin":
          return this.$t("users.table.institution_admin");
        case "InstitutionManager":
          return this.$t("users.table.institution_manager");
        case "User":
          return this.$t("users.table.operator");
        case "EbayOperator":
          return this.$t("users.table.ebay_operator");
        default:
          return "";
      }
    },
  },
};
</script>

<style scoped>
.nowrap-headers th {
  white-space: nowrap;
}
.users-table-host {
  position: relative;
  width: 100%;
  min-width: 0;
}
.users-table-host :deep(table) {
  width: max-content;
  min-width: 100%;
}
.users-table-host :deep(.v-data-table-footer) {
  min-width: max-content;
}
.tableRowColor {
  background-color: #f5f5f5;
}
.tableRowColorSelected {
  background-color: #e0e0e0;
}
.pointer {
  cursor: pointer;
}

@media (max-width: 959.98px) {
  .users-table-host :deep(.v-table__wrapper),
  .users-table-host :deep(.v-data-table__wrapper),
  .users-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .users-table-host :deep(.global-sticky-hscroll) {
    display: none !important;
  }
  .users-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .users-mobile-list {
    display: none !important;
  }
}

.users-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.users-mobile-card--selected {
  outline: 2px solid rgb(var(--v-theme-primary));
}
.users-mobile-card__main {
  display: flex;
  align-items: flex-start;
  padding: 10px;
  gap: 10px;
}
.users-mobile-card__info {
  flex: 1;
  min-width: 0;
}
.users-mobile-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.users-mobile-card__header-left {
  min-width: 0;
  flex: 1 1 auto;
}
.users-mobile-card__header-right {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.users-mobile-card__created-btn {
  height: auto !important;
  min-height: 0 !important;
  text-transform: none;
  letter-spacing: normal;
}
.users-mobile-card__status-chip {
  max-width: 100%;
}
.users-mobile-parts-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
}
.users-mobile-parts-item {
  flex: 1 1 0;
  min-width: 0;
}
.users-mobile-parts-item.text-right {
  text-align: right;
}
.users-mobile-card__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  padding: 4px 6px;
}
</style>
